import { FC, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import type { FlightFilters as FlightFiltersType } from '../../../../../types/flight.types';
import {
  StopOption,
  FLIGHT_SORT_SELECT_OPTIONS,
  toFlightSortSelectValue,
  parseFlightSortSelectValue,
  STOP_OPTIONS,
  AirlineOption,
  AirportOption,
} from '../../../../../types/flight.types';

import styles from './FlightFilters.module.scss';

const buildTimeFilterOptions = (): { value: string; label: string }[] => {
  const opts: { value: string; label: string }[] = [{ value: '', label: 'Any time' }];
  for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const d = new Date(2000, 0, 1, h, m);
    const label = d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    opts.push({ value, label });
  }
  return opts;
};

const TIME_FILTER_OPTIONS = buildTimeFilterOptions();

interface FlightFiltersProps {
  filters: FlightFiltersType;
  onFiltersChange: (filters: FlightFiltersType) => void;
  airlineOptions: AirlineOption[];
  airportOptions: AirportOption[];
}

type FilterSectionKey = 'sort' | 'stops' | 'airlines' | 'airports' | 'time';

const INITIAL_SECTION_OPEN: Record<FilterSectionKey, boolean> = {
  sort: true,
  stops: true,
  airlines: true,
  airports: true,
  time: true,
};

const FilterCheckboxUnchecked = () => <span className={styles.filterCheckboxUnchecked} aria-hidden />;

const FilterCheckboxChecked = () => (
  <span className={styles.filterCheckboxChecked} aria-hidden>
    <span className={styles.filterCheckboxCheckmark} />
  </span>
);

const FlightFilters: FC<FlightFiltersProps> = ({ filters, onFiltersChange, airlineOptions, airportOptions }) => {
  const [openSections, setOpenSections] = useState<Record<FilterSectionKey, boolean>>(INITIAL_SECTION_OPEN);

  const toggleSection = (key: FilterSectionKey) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sectionHeader = (key: FilterSectionKey, title: string) => (
    <Box
      className={`${styles.filterHeader} ${openSections[key] ? styles.filterHeaderExpanded : ''}`}
      onClick={() => toggleSection(key)}
      role="button"
      tabIndex={0}
      aria-expanded={openSections[key]}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSection(key);
        }
      }}
    >
      <Typography
        component="span"
        className={styles.filterTitle}
      >
        {title}
      </Typography>
      <KeyboardArrowDownIcon
        className={`${styles.expandIcon} ${openSections[key] ? styles.expandIconOpen : ''}`}
        aria-hidden
      />
    </Box>
  );
  const handleSortChange = (event: SelectChangeEvent<string>) => {
    const { sortBy, sortOrder } = parseFlightSortSelectValue(event.target.value);
    onFiltersChange({
      ...filters,
      sortBy,
      sortOrder,
    });
  };

  const sortSelectValue = toFlightSortSelectValue(filters.sortBy, filters.sortOrder ?? 'asc');

  const handleStopToggle = (stop: StopOption) => {
    const newStops = filters.stops.includes(stop) ? filters.stops.filter(s => s !== stop) : [...filters.stops, stop];
    onFiltersChange({
      ...filters,
      stops: newStops,
    });
  };

  const handleAirlineToggle = (airlineCode: string) => {
    const newAirlines = filters.airlines.includes(airlineCode)
      ? filters.airlines.filter(a => a !== airlineCode)
      : [...filters.airlines, airlineCode];
    onFiltersChange({
      ...filters,
      airlines: newAirlines,
    });
  };

  const handleAirportToggle = (airportCode: string) => {
    const newAirports = filters.airports.includes(airportCode)
      ? filters.airports.filter(a => a !== airportCode)
      : [...filters.airports, airportCode];
    onFiltersChange({
      ...filters,
      airports: newAirports,
    });
  };

  const handleTimeFromSelect = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      timeRange: {
        ...filters.timeRange,
        startFrom: event.target.value,
      },
    });
  };

  const handleTimeToSelect = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      timeRange: {
        ...filters.timeRange,
        upTo: event.target.value,
      },
    });
  };

  const renderTimeSelectValue = (selected: string) => {
    if (selected === '') {
      return <span className={styles.timeSelectPlaceholder}>Any time</span>;
    }
    return TIME_FILTER_OPTIONS.find(o => o.value === selected)?.label ?? selected;
  };

  return (
    <Box className={styles.filtersContainer}>
      <Box className={styles.filterSection}>
        {sectionHeader('sort', 'Sort By')}
        <Collapse
          in={openSections.sort}
          timeout={280}
          unmountOnExit={false}
        >
          <FormControl
            fullWidth
            onClick={e => e.stopPropagation()}
          >
            <Select
              value={sortSelectValue}
              onChange={handleSortChange}
              className={styles.select}
              renderValue={selected =>
                FLIGHT_SORT_SELECT_OPTIONS.find(o => o.value === selected)?.label ?? String(selected)
              }
              MenuProps={{
                classes: { paper: styles.selectMenu },
              }}
            >
              {FLIGHT_SORT_SELECT_OPTIONS.map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className={styles.menuItem}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('stops', 'Stops')}
        <Collapse
          in={openSections.stops}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {STOP_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.stops.includes(option.value)}
                    onChange={() => handleStopToggle(option.value)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{option.label}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('airlines', 'Airlines')}
        <Collapse
          in={openSections.airlines}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {airlineOptions.map(airline => (
              <FormControlLabel
                key={airline.iata_code}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.airlines.includes(airline.iata_code)}
                    onChange={() => handleAirlineToggle(airline.iata_code)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{airline.name}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('airports', 'Airports')}
        <Collapse
          in={openSections.airports}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {airportOptions.map((airport, index) => (
              <FormControlLabel
                key={airport.iata_code || index}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.airports.includes(airport.iata_code)}
                    onChange={() => handleAirportToggle(airport.iata_code)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{airport.name}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('time', 'Time Range')}
        <Collapse
          in={openSections.time}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.timeRangeInputs}>
            <Box className={styles.timeInput}>
              <Typography
                component="span"
                className={styles.timeLabel}
              >
                Start From
              </Typography>
              <FormControl
                fullWidth
                onClick={e => e.stopPropagation()}
              >
                <Select
                  value={filters.timeRange.startFrom}
                  onChange={handleTimeFromSelect}
                  displayEmpty
                  className={styles.select}
                  renderValue={selected => renderTimeSelectValue(selected as string)}
                  MenuProps={{
                    classes: { paper: styles.selectMenu },
                    PaperProps: { style: { maxHeight: 320 } },
                  }}
                >
                  {TIME_FILTER_OPTIONS.map(option => (
                    <MenuItem
                      key={`start-${option.value || 'any'}`}
                      value={option.value}
                      className={styles.menuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className={styles.timeInput}>
              <Typography
                component="span"
                className={styles.timeLabel}
              >
                Up To
              </Typography>
              <FormControl
                fullWidth
                onClick={e => e.stopPropagation()}
              >
                <Select
                  value={filters.timeRange.upTo}
                  onChange={handleTimeToSelect}
                  displayEmpty
                  className={styles.select}
                  renderValue={selected => renderTimeSelectValue(selected as string)}
                  MenuProps={{
                    classes: { paper: styles.selectMenu },
                    PaperProps: { style: { maxHeight: 320 } },
                  }}
                >
                  {TIME_FILTER_OPTIONS.map(option => (
                    <MenuItem
                      key={`to-${option.value || 'any'}`}
                      value={option.value}
                      className={styles.menuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default FlightFilters;
